import React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import LineExclamationCircleIcon from 'LuxKit/Icons/line/LineExclamationCircleIcon'
import Group from 'components/utils/Group'

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: ${rem(24)};
  text-align: center;
  background-color: ${props => props.theme.palette.neutral.default.one};
`

const IconContainer = styled.div`
  color: ${props => props.theme.palette.messaging.critical.normalForeground};
`

interface Props {
  error?: Error
  onRetry?: () => void
  onClose?: () => void
}

function WrappedErrorState({ error, onRetry, onClose }: Props) {
  const intl = useIntl()
  const isNotFound = error && 'status' in error && error.status === 404

  const headingText = isNotFound
    ? intl.formatMessage({ id: 'JfsKxaL+6h', defaultMessage: 'Wrapped not found', description: 'Error heading when Wrapped link is not found' })
    : intl.formatMessage({ id: 'fgo4Rb2pIZ', defaultMessage: 'Something went wrong', description: 'Generic error heading for Wrapped page' })

  const bodyText = isNotFound
    ? intl.formatMessage({ id: '+NvKqvR68B', defaultMessage: 'This Wrapped link may have expired or is no longer available.', description: 'Error message when Wrapped link is expired or unavailable' })
    : intl.formatMessage({ id: 'Vrct33jQli', defaultMessage: 'We couldn\u2019t load your year in review. Please try again.', description: 'Error message when Wrapped content fails to load' })

  return (
    <Container>
      <Group direction="vertical" gap={24} horizontalAlign="center">
        <IconContainer>
          <LineExclamationCircleIcon size={48} />
        </IconContainer>
        <Group direction="vertical" gap={8} horizontalAlign="center">
          <Heading variant="heading4">
            {headingText}
          </Heading>
          <BodyText variant="medium" colour="neutral-two">
            {bodyText}
          </BodyText>
        </Group>
        <Group direction="horizontal" gap={12}>
          {onRetry && !isNotFound && (
            <TextButton kind="primary" onClick={onRetry}>
              {intl.formatMessage({ id: 'Zp+cXQ0zM9', defaultMessage: 'Try again', description: 'Button label to retry loading Wrapped content' })}
            </TextButton>
          )}
          {onClose && (
            <TextButton kind="secondary" onClick={onClose}>
              {intl.formatMessage({ id: 'PyDwDFWwNv', defaultMessage: 'Close', description: 'Close button label' })}
            </TextButton>
          )}
        </Group>
      </Group>
    </Container>
  )
}

export default WrappedErrorState
